import React from 'react';
import { useTable } from 'react-table';
import { Switch, Tooltip } from 'antd';
import { HomeOutlined, BankOutlined, CheckCircleOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import { FiShoppingCart, FiPlusCircle, FiMinusCircle } from 'react-icons/fi';

const TableStyles = styled.div`
  overflow-x: auto;

  table {
    width: 100%;
    min-width: 760px;
    border-collapse: collapse;
  }
  thead {
    background: #f0f2f5;
  }
  th {
    text-align: left;
    padding: 12px 15px;
    border-bottom: 1px solid #e5e7eb;
    font-weight: bold;
    color: #1e3a8a;
  }
  td {
    padding: 8px 15px;
    border-bottom: 1px solid #e5e7eb;
  }
  tr:hover {
    background: #172554;
    color: #c29946;
  }
  tr.in-cart {
    background: #172554;
    color: #c29946;
  }
`;

const StatusButton = styled.div`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  background-color: ${props => (props.active ? '#e0f2fe' : '')};
  color: ${props => (props.active ? '#2563eb' : 'gray')};
  border: 1px solid ${props => (props.active ? '#bae6fd' : '#bbf7d0')};
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;

  .icon {
    margin-right: 4px;
  }
`;

const CartButton = styled.div`
  position: relative;
  display: inline-block;

  .cart-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    background: #c29946;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    transition: all 0.3s;
    color: #1e3a8a;

    &:hover {
      opacity: 0.9;
    }

    .cart-icon {
      width: 24px;
      height: 24px;
    }
  }

  .indicator-wrapper {
    position: absolute;
    top: -4px;
    right: -4px;
    background: #c29946;
    border-radius: 50%;
    padding: 2px;

    .indicator-icon {
      width: 14px;
      height: 14px;
      color: #1e3a8a;
    }
  }
`;

const EMPTY_STATUS_UPDATING_IDS = Object.freeze([]);

const PropertyTable = ({
  properties,
  cart = {},
  addToFromCart,
  removeFromCart,
  onToggleStatus,
  onRecurringOrders,
  canManageStatus = false,
  statusUpdatingIds = EMPTY_STATUS_UPDATING_IDS
}) => {
  const isPropertyInCart = React.useCallback((property) => cart && cart[property.id], [cart]);
  const isPropertyActive = React.useCallback((property) => (
    property?.status === '1' || property?.status === 1 || property?.status === true
  ), []);
  const updatingStatusIds = React.useMemo(() => new Set(statusUpdatingIds), [statusUpdatingIds]);

  const columns = React.useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'property_title',
        Cell: ({ row, value }) => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {canManageStatus && (
              <Switch
                checked={row.original.status === '1' || row.original.status === 1}
                onChange={(checked) => onToggleStatus(row.original.id, checked ? '1' : '0')}
                loading={updatingStatusIds.has(row.original.id)}
                disabled={updatingStatusIds.has(row.original.id)}
                size="small"
                style={{ marginRight: '10px' }}
              />
            )}
            <span>{value || 'Unnamed Property'}</span>
          </div>
        ),
      },
      {
        Header: 'Address',
        accessor: 'apartment_address',
        Cell: ({ row, value }) => (
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {row.original.property_type_id === 1 ? (
              <HomeOutlined style={{ marginRight: '10px', color: '#60a5fa' }} />
            ) : (
              <BankOutlined style={{ marginRight: '10px', color: '#60a5fa' }} />
            )}
            <span>{value || 'No address'}</span>
          </div>
        ),
      },
      {
        Header: 'Agency',
        accessor: 'agency_company_name',
        Cell: ({ value }) => <span>{value || '—'}</span>,
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({ value }) => (
          <StatusButton active={value === '1' || value === 1}>
            <CheckCircleOutlined className="icon" />
            {value === '1' || value === 1 ? 'Active' : 'Inactive'}
          </StatusButton>
        ),
      },
      {
        Header: 'Code',
        accessor: 'delivery_zip_code',
        Cell: ({ value }) => <span>{value || 'N/A'}</span>,
      },
      {
        Header: 'Add to Cart',
        id: 'actions',
        Cell: ({ row }) => {
          const property = row.original;
          const inCart = isPropertyInCart(property);
          const isActive = isPropertyActive(property);
          const cartActionDisabled = !isActive && !inCart;

          return (
            <div className="flex justify-center">
              <Tooltip title={cartActionDisabled ? 'Inactive properties cannot be added to cart' : ''}>
                <CartButton>
                  <button
                    className="cart-button"
                    disabled={cartActionDisabled}
                    onClick={() => (inCart ? removeFromCart(property.id) : addToFromCart(property))}
                    style={cartActionDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                  >
                    <FiShoppingCart className="cart-icon" />
                  </button>
                  <div className="indicator-wrapper">
                    {inCart ? (
                      <FiMinusCircle className="indicator-icon" />
                    ) : (
                      <FiPlusCircle className="indicator-icon" />
                    )}
                  </div>
                </CartButton>
              </Tooltip>
            </div>
          );
        },
      },
      ...(onRecurringOrders ? [{
        Header: 'Recurring orders',
        id: 'recurrence',
        Cell: ({ row }) => <button type="button" disabled={!isPropertyActive(row.original)}
          aria-label={`Recurring orders for ${row.original.property_title}`}
          onClick={() => onRecurringOrders(row.original)}
          className="rounded border border-gray-300 px-3 py-2 text-sm disabled:opacity-50">
          Recurring orders
        </button>,
      }] : []),
    ],
    [addToFromCart, canManageStatus, isPropertyActive, isPropertyInCart, onToggleStatus, onRecurringOrders, removeFromCart, updatingStatusIds]
  );

  const data = React.useMemo(() => properties || [], [properties]);
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable({
    columns,
    data,
  });

  return (
    <>
      <div className="md:hidden space-y-3">
        {(properties || []).map((property) => {
          const inCart = isPropertyInCart(property);
          const isActive = isPropertyActive(property);
          const cartActionDisabled = !isActive && !inCart;
          return (
            <div
              key={property.id}
              className={`rounded-lg border p-3 shadow-sm ${inCart ? 'bg-golden-navy text-white border-golden-navy' : 'bg-white border-gray-200'}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {property.property_type_id === 1 ? (
                      <HomeOutlined className="text-blue-400" />
                    ) : (
                      <BankOutlined className="text-blue-400" />
                    )}
                    <h3 className={`font-semibold truncate ${inCart ? 'text-golden-yellow' : 'text-golden-navy'}`}>
                      {property.property_title || 'Unnamed Property'}
                    </h3>
                  </div>
                  <p className={`mt-2 text-sm ${inCart ? 'text-gray-200' : 'text-gray-600'}`}>
                    {[property.apartment_number, property.apartment_address].filter(Boolean).join(', ') || 'No address'}
                  </p>
                  {property.agency_company_name && (
                    <p className={`text-xs font-medium ${inCart ? 'text-white' : 'text-golden-navy'}`}>
                      {property.agency_company_name}
                    </p>
                  )}
                  <p className={`text-xs ${inCart ? 'text-gray-300' : 'text-gray-500'}`}>
                    {property.delivery_zip_code || property.postcode || 'N/A'}
                  </p>
                </div>

                {canManageStatus && (
                  <Switch
                    checked={isActive}
                    onChange={(checked) => onToggleStatus(property.id, checked ? '1' : '0')}
                    loading={updatingStatusIds.has(property.id)}
                    disabled={updatingStatusIds.has(property.id)}
                    size="small"
                  />
                )}
              </div>

              <div className="mt-3 flex items-center justify-between gap-3">
                <StatusButton active={isActive}>
                  <CheckCircleOutlined className="icon" />
                  {isActive ? 'Active' : 'Inactive'}
                </StatusButton>
                <CartButton>
                  <button
                    className="cart-button"
                    disabled={cartActionDisabled}
                    onClick={() => (inCart ? removeFromCart(property.id) : addToFromCart(property))}
                    style={cartActionDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                  >
                    <FiShoppingCart className="cart-icon" />
                  </button>
                  <div className="indicator-wrapper">
                    {inCart ? (
                      <FiMinusCircle className="indicator-icon" />
                    ) : (
                      <FiPlusCircle className="indicator-icon" />
                    )}
                  </div>
                </CartButton>
              </div>
              {onRecurringOrders && <button type="button" disabled={!isActive}
                aria-label={`Recurring orders for ${property.property_title}`}
                onClick={() => onRecurringOrders(property)}
                className="mt-3 w-full rounded border border-gray-300 px-3 py-2 text-sm disabled:opacity-50">
                Recurring orders
              </button>}
            </div>
          );
        })}
      </div>

      <TableStyles className="hidden md:block">
        <table {...getTableProps()}>
          <thead>
            {headerGroups.map((headerGroup) => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <th {...column.getHeaderProps()}>{column.render('Header')}</th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            {rows.map((row) => {
              prepareRow(row);
              const inCart = isPropertyInCart(row.original);
              return (
                <tr {...row.getRowProps()} className={inCart ? 'in-cart' : ''}>
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableStyles>
    </>
  );
};

export default PropertyTable;
